import { renderCameraTooltipContent, renderFieldTooltipContent } from './render-tooltip';
import type { MapApiCameraTooltip, MapApiTooltip, MapPageDom, MapState, TooltipApi } from './types';

/** Horizontal pointer-to-tooltip gap in viewport CSS pixels. */
const TOOLTIP_OFFSET_X = 16;
/** Vertical pointer-to-tooltip gap in viewport CSS pixels. */
const TOOLTIP_OFFSET_Y = 16;
/** Minimum tooltip distance from a viewport edge, in CSS pixels. */
const TOOLTIP_VIEWPORT_PADDING = 12;

/**
 * Places the tooltip near the pointer and keeps it inside the current viewport.
 *
 * @param params Tooltip root and latest viewport-relative pointer coordinates.
 * @returns Nothing. Mutates only inline `left` and `top` position styles of the supplied root.
 */
function positionTooltip(params: {
  tooltipRoot: HTMLElement;
  pointerX: number;
  pointerY: number;
}): void {
  const { tooltipRoot, pointerX, pointerY } = params;
  const tooltipBounds = tooltipRoot.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  let left = pointerX + TOOLTIP_OFFSET_X;
  let top = pointerY + TOOLTIP_OFFSET_Y;

  if (left + tooltipBounds.width > viewportWidth - TOOLTIP_VIEWPORT_PADDING) {
    left = pointerX - tooltipBounds.width - TOOLTIP_OFFSET_X;
  }

  if (top + tooltipBounds.height > viewportHeight - TOOLTIP_VIEWPORT_PADDING) {
    top = pointerY - tooltipBounds.height - TOOLTIP_OFFSET_Y;
  }

  left = Math.max(TOOLTIP_VIEWPORT_PADDING, left);
  top = Math.max(TOOLTIP_VIEWPORT_PADDING, top);

  tooltipRoot.style.left = `${left}px`;
  tooltipRoot.style.top = `${top}px`;
}

/**
 * Renders tooltip content, shows the shared tooltip root and positions it near the pointer.
 *
 * @param params Tooltip root, renderer callback and latest viewport-relative pointer coordinates.
 * @returns Nothing. Replaces tooltip content, reveals the root and positions it.
 */
function showTooltip(params: {
  tooltipRoot: HTMLElement;
  renderContent: () => void;
  pointerX: number;
  pointerY: number;
}): void {
  const { tooltipRoot, renderContent, pointerX, pointerY } = params;

  renderContent();
  tooltipRoot.classList.remove('hidden');
  tooltipRoot.setAttribute('aria-hidden', 'false');
  positionTooltip({
    tooltipRoot,
    pointerX,
    pointerY,
  });
}

/**
 * Initializes the tooltip root and returns a public API for hover scenarios.
 *
 * @param params DOM references and runtime state for the `/map` page.
 * @returns Tooltip facade used by page-owned pointer interactions; it owns no global listeners.
 */
export function initializeTooltip(params: { mapDom: MapPageDom; state: MapState }): TooltipApi {
  const { tooltipRoot, tooltipContent } = params.mapDom;
  const { state } = params;

  tooltipRoot.className = 'pointer-events-none fixed z-[70] hidden';
  tooltipRoot.setAttribute('aria-hidden', 'true');
  tooltipContent.textContent = '';

  return {
    showField(showParams: { tooltipData: MapApiTooltip; pointerX: number; pointerY: number }) {
      showTooltip({
        tooltipRoot,
        renderContent: () => {
          renderFieldTooltipContent({
            tooltipContent,
            tooltipData: showParams.tooltipData,
            state,
          });
        },
        pointerX: showParams.pointerX,
        pointerY: showParams.pointerY,
      });
    },
    showCamera(showParams: {
      tooltipData: MapApiCameraTooltip;
      pointerX: number;
      pointerY: number;
    }) {
      showTooltip({
        tooltipRoot,
        renderContent: () => {
          renderCameraTooltipContent({
            tooltipContent,
            tooltipData: showParams.tooltipData,
            state,
          });
        },
        pointerX: showParams.pointerX,
        pointerY: showParams.pointerY,
      });
    },
    move(moveParams: { pointerX: number; pointerY: number }) {
      if (tooltipRoot.classList.contains('hidden')) {
        return;
      }

      positionTooltip({
        tooltipRoot,
        pointerX: moveParams.pointerX,
        pointerY: moveParams.pointerY,
      });
    },
    hide() {
      tooltipContent.textContent = '';
      tooltipRoot.classList.add('hidden');
      tooltipRoot.setAttribute('aria-hidden', 'true');
    },
  };
}
